import { fetchBaseQuery } from '@reduxjs/toolkit/dist/query';
import { Zeroconf, ZeroconfResult } from '@awesome-cordova-plugins/zeroconf';
import { Subscription } from 'rxjs';
import { ResponseBase } from './request';
import { localStorageService } from './localStorageService';

const SECOND = 1000;

export const CLOUD_FETCH_TIMEOUT = 30 * SECOND;
export const LOCAL_FETCH_TIMEOUT = 5 * SECOND;

export const POLLING_INTERVAL = 30 * SECOND;

export const LIGHTS_DIMMER_DELAY = 150;
export const LIGHT_DIMMER_DELAY = 25;

const logZeroConf = (message?: unknown, ...optionalParams: unknown[]) =>
  console.log(`[${Date.now()}]`, 'Zeroconf:', message, ...optionalParams);

const getRemoteBaseUrl = () => {
  switch (window.__RUNTIME_CONFIG__.REACT_APP_MGH_ENV) {
    case 'prod':
      return 'https://api.ultra.myglobalhome.co';
    case 'uat':
      return 'https://api.uat.mgh.beintrepid.co.uk';
    case 'qa':
      return 'https://api.qa.mgh.beintrepid.co.uk';
    default:
      return 'https://api.test.mgh.beintrepid.co.uk';
  }
};

export const baseUrl = getRemoteBaseUrl();
export const appVersion = window.__RUNTIME_CONFIG__.REACT_APP_MGH_VERSION;
export const bonjourServiceType =
  window.__RUNTIME_CONFIG__.REACT_APP_MGH_BONJOUR_SERVICE_TYPE || '_mgh-control._tcp.';

logZeroConf('Service type:', bonjourServiceType);

let watchSubscription: Subscription | null;
let localAddress: string | null = null;
let watchIntervalId: string | number | NodeJS.Timeout | undefined;

const startWatchingZeroConfCore = async (
  unitId?: string,
  onServiceResolved?: (result: ZeroconfResult) => void,
  onLocalEdgeIPAddress?: (address: string) => void,
  onLocalEdgeIPAddressMissing?: () => void,
  onLocalEdgeIPAddressPingFailed?: () => void,
) => {
  logZeroConf('StartWatchingZeroConf....');
  if (!unitId) {
    logZeroConf(`Missing unitId (${unitId}). Not starting ZeroConfWatch...`);
    return;
  }

  const localEdgeIPAddress = localStorageService.getEdgeLocalIPAddress();

  if (localEdgeIPAddress) {
    logZeroConf('have stored last successful ipAddress', localEdgeIPAddress);
    logZeroConf('trying to ping last successful ipAddress', localEdgeIPAddress);

    if (await pingEdgeController(localEdgeIPAddress)) {
      localAddress = localEdgeIPAddress;
      logZeroConf(
        'received response from controller using last successful ipAddress. Switching to localAddress:',
        localAddress,
      );
      onLocalEdgeIPAddress?.(localEdgeIPAddress);
      return;
    } else {
      onLocalEdgeIPAddressPingFailed?.();
      logZeroConf('Not able to get response from controller', localEdgeIPAddress);
    }
  } else {
    logZeroConf('No last successful ipAddress stored');
    onLocalEdgeIPAddressMissing?.();
  }

  if (!watchSubscription) {
    logZeroConf('Starting watch...');
    watchSubscription = Zeroconf.watch(bonjourServiceType, 'local.').subscribe(async (result) => {
      if (result.action === 'resolved') {
        logZeroConf(
          'Discovered zeroconf service',
          result.action,
          result.service.name,
          result.service,
        );

        if (localAddress) {
          logZeroConf('service resolved but we are already connected locally');
          return;
        }

        if (result.service.txtRecord.uuid === unitId) {
          const address = await findEdgeController(result);
          if (address) {
            localAddress = address;
            localStorageService.setEdgeLocalIPAddress(address);
            logZeroConf(
              'using serviceInfo as local:',
              result.service,
              'and use address:',
              localAddress,
            );
            onLocalEdgeIPAddress?.(localAddress);
            onServiceResolved?.(result);
          }
        } else {
          logZeroConf(
            `UnitId (${unitId}) doesn't match with discovered service UnitId (${result.service.txtRecord.uuid})`,
          );
        }
      } else if (result.action === 'removed') {
        logZeroConf('service removed', result.service, result.service.name);
        localAddress = null;
        watchSubscription = null;
        onServiceResolved && onServiceResolved(result);
      }
    });

    logZeroConf('watch started.');
  }

  return watchSubscription;
};

let interval = 5 * SECOND;

export const startWatchingZeroConf = async (
  unitId?: string,
  onServiceResolved?: (result: ZeroconfResult) => void,
  onLocalEdgeIPAddress?: (address: string) => void,
  onLocalEdgeIPAddressMissing?: () => void,
  onLocalEdgeIPAddressPingFailed?: () => void,
) => {
  logZeroConf('Setting up interval to browse bonjour services...');

  const restartWatch = async () => {
    await stopWatchingZeroConfCore();
    await startWatchingZeroConfCore(
      unitId,
      onServiceResolved,
      onLocalEdgeIPAddress,
      onLocalEdgeIPAddressMissing,
      onLocalEdgeIPAddressPingFailed,
    );
  };

  const callback = async () => {
    await restartWatch();

    if (interval < 30 * SECOND) {
      clearInterval(watchIntervalId);

      interval = interval * 2 > 30 * SECOND ? 30 * SECOND : interval * 2;

      watchIntervalId = setInterval(callback, interval);
    }
  };

  await restartWatch();
  watchIntervalId = setInterval(callback, interval);
};

export const resetLocalAddress = () => {
  watchSubscription?.unsubscribe();
  watchSubscription = null;
  localAddress = null;
};

export const stopWatchingZeroConfCore = async () => {
  Zeroconf.unwatch(bonjourServiceType, 'local.');
  resetLocalAddress();
  logZeroConf('Watch stopped.');
};

export const stopWatchingZeroConf = async () => {
  logZeroConf('Stopping interval and ZeroConf watch...');
  clearInterval(watchIntervalId);
  return stopWatchingZeroConfCore();
};

const buildLocalUrl = (localAddress: string | null) => localAddress && localAddress;

export const getBaseUrl = () => buildLocalUrl(localAddress) || baseUrl;
export const isLocalConnection = () => !!localAddress;

export const mghFetchBaseQuery = (args: Parameters<typeof fetchBaseQuery>[0]) => {
  const fetchBaseQueryFunc = fetchBaseQuery({
    ...args,
    timeout: CLOUD_FETCH_TIMEOUT,
  });

  return async (...args: Parameters<typeof fetchBaseQueryFunc>) => {
    const url = getBaseUrl();
    console.log('ULTRA url:', url, localAddress);
    const [fetchArgs, api, ...restArgs] = args;
    const modifiedFetchArg =
      typeof fetchArgs === 'string'
        ? `${url}/${fetchArgs}`
        : { ...fetchArgs, url: `${url}/${fetchArgs.url}` };
    const modifiedArgs: Parameters<typeof fetchBaseQueryFunc> = [
      modifiedFetchArg,
      api,
      ...restArgs,
    ];
    const timeout = isLocalConnection() ? LOCAL_FETCH_TIMEOUT : CLOUD_FETCH_TIMEOUT;
    const timeoutId = setTimeout(() => api.abort(), timeout);
    try {
      return await fetchBaseQueryFunc(...modifiedArgs);
    } finally {
      clearTimeout(timeoutId);
    }
  };
};

export const authFetchBaseQuery = (args: Parameters<typeof fetchBaseQuery>[0]) => {
  const mghFetchBaseQueryFn = mghFetchBaseQuery({
    prepareHeaders: (headers) => {
      headers.set('Authorization', `Bearer ${localStorageService.getTokens()?.accessToken}`);
      return headers;
    },
    ...args,
  });

  const authFetchBaseQueryFn = fetchBaseQuery({
    ...args,
    timeout: CLOUD_FETCH_TIMEOUT,
  });

  return async (...args: Parameters<typeof mghFetchBaseQueryFn>) => {
    const url = getBaseUrl();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, ...restArgs] = args;

    const result = await mghFetchBaseQueryFn(...args);
    if (result.error && result.error.status === 401) {
      const errorData = result.error.data as ResponseBase;
      if (errorData && errorData.message === 'Authentication Token expired or invalid') {
        const refreshTokenResult = await authFetchBaseQueryFn(
          {
            url: `${url}/user/token/refresh`,
            method: 'POST',
            body: {
              email: localStorageService.getUser(),
              token: localStorageService.getTokens()?.refreshToken,
            },
          },
          ...restArgs,
        );

        if (refreshTokenResult.error) {
          console.error(refreshTokenResult.error);
          // logout ?
          localStorageService.clearTokens();
          location.reload();
        }

        if (refreshTokenResult.data) {
          const refreshTokenData = refreshTokenResult.data as { token: string };
          localStorageService.setAccessToken(refreshTokenData.token);
          return mghFetchBaseQueryFn(...args);
        }
      }
    }

    return result;
  };
};

const findEdgeController = async (result: ZeroconfResult) => {
  for (const address of result.service.ipv4Addresses) {
    const baseUrl = `http://${address}:${result.service.port}`;
    if (await pingEdgeController(baseUrl)) {
      return baseUrl;
    }
  }

  for (const address of result.service.ipv6Addresses) {
    const baseUrl = `http://[${address}]:${result.service.port}`;
    if (await pingEdgeController(baseUrl)) {
      return baseUrl;
    }
  }
};

const pingEdgeController = async (baseUrl: string) => {
  const timeout = 5 * SECOND;
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), timeout);
  const reqId = (Math.random() + 1).toString(36).substring(10);
  try {
    logZeroConf(`[${reqId}] Testing connection to address:`, baseUrl);
    await fetch(`${baseUrl}/status`, {
      headers: { Authorization: `Bearer ${localStorageService.getTokens()?.accessToken}` },
      signal: abortController.signal,
    });
    logZeroConf(`[${reqId}] connected!`, baseUrl);
    return true;
  } catch (err) {
    logZeroConf(`[${reqId}] Not able to connect to`, baseUrl, 'Error:', err);
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};
